import { X, Printer, ShieldCheck, Download, ExternalLink, Award, Calendar, MapPin, Activity } from "lucide-react";
import { FlagIcon } from "./FlagIcon";
import { AthleteRadarChart, type AthleteAttributes } from "./AthleteRadarChart";

export interface DossierAthlete {
  id: number | string;
  name: string;
  sport: string;
  position: string;
  nationality: string;
  countryCode?: string;
  age?: number | null;
  height?: string;
  dominantFoot?: string;
  currentClub?: string;
  bio?: string;
  imageUrl?: string;
  videoUrl?: string;
  achievements?: string[];
  attributes?: AthleteAttributes;
}

interface AthleteDossierModalProps {
  isOpen: boolean;
  onClose: () => void;
  athlete: DossierAthlete | null;
}

export function AthleteDossierModal({ isOpen, onClose, athlete }: AthleteDossierModalProps) {
  if (!isOpen || !athlete) return null;

  const handlePrint = () => {
    window.print();
  };

  const countryCode = athlete.countryCode || (athlete.nationality.toLowerCase().includes("germany") ? "DE" : athlete.nationality.toLowerCase().includes("nigeria") ? "NG" : athlete.nationality.toLowerCase().includes("united states") || athlete.nationality.toLowerCase().includes("usa") ? "US" : athlete.nationality.toLowerCase().includes("canada") ? "CA" : athlete.nationality.toLowerCase().includes("uk") || athlete.nationality.toLowerCase().includes("england") ? "GB" : "DE");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/90 backdrop-blur-md overflow-y-auto print:p-0 print:bg-white animate-in fade-in duration-200">
      {/* Backdrop */}
      <div className="fixed inset-0 print:hidden" onClick={onClose} />

      <div className="relative w-full max-w-4xl bg-slate-950 text-white border border-yellow-500/40 rounded-3xl overflow-hidden shadow-2xl z-10 print:border-none print:shadow-none print:bg-white print:text-black my-auto">
        {/* Action Header Bar (Hidden in Print) */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/90 print:hidden">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-md bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 text-xs font-bold uppercase tracking-wider">
              Official Player Profile
            </span>
            <span className="text-slate-400 text-xs font-mono">Ref: ESA-ATH-{athlete.id}</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-extrabold transition-colors shadow-lg"
            >
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Dossier Sheet */}
        <div className="p-6 sm:p-10 space-y-8 print:p-8 print:text-black print:space-y-6">
          {/* Dossier Agency Brand Topbar */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-6 print:border-black">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-yellow-400 print:text-amber-700">
                Eliada Sports Agency • Talent Management
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-white print:text-black tracking-tight mt-0.5">
                Official Athlete Profile & Career Overview
              </h2>
            </div>
            <div className="text-right hidden sm:block">
              <div className="text-xs text-slate-400 print:text-slate-600">Offices: Germany • UK • USA • Canada • Nigeria</div>
              <div className="text-xs text-yellow-400 font-mono font-bold print:text-amber-700">scouting@eliadasportsagency.com</div>
            </div>
          </div>

          {/* Profile Overview (Photo + Key Data + Radar) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* Left Photo & Core Stats (4 cols) */}
            <div className="md:col-span-4 space-y-4">
              <div className="aspect-[3/4] w-full rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 relative shadow-xl print:border-gray-300">
                <img
                  src={athlete.imageUrl || "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&auto=format&fit=crop"}
                  alt={athlete.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 right-3 bg-slate-950/80 backdrop-blur-md px-2.5 py-1 rounded-full border border-slate-700 flex items-center gap-1.5">
                  <FlagIcon country={countryCode} size="sm" />
                  <span className="text-[11px] font-bold text-white uppercase">{countryCode}</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-2 text-xs print:bg-gray-50 print:border-gray-300">
                <div className="flex justify-between py-1 border-b border-slate-800/80 print:border-gray-200">
                  <span className="text-slate-400 print:text-slate-600">Position</span>
                  <strong className="text-white print:text-black">{athlete.position || "Forward"}</strong>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800/80 print:border-gray-200">
                  <span className="text-slate-400 print:text-slate-600">Age</span>
                  <strong className="text-white print:text-black">{athlete.age ? `${athlete.age} yrs` : "Under 23"}</strong>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800/80 print:border-gray-200">
                  <span className="text-slate-400 print:text-slate-600">Preferred Foot</span>
                  <strong className="text-white print:text-black">{athlete.dominantFoot || "Right / Both"}</strong>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800/80 print:border-gray-200">
                  <span className="text-slate-400 print:text-slate-600">Nationality</span>
                  <strong className="text-white print:text-black">{athlete.nationality}</strong>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-slate-400 print:text-slate-600">Representation</span>
                  <strong className="text-emerald-400 print:text-emerald-700 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Exclusive
                  </strong>
                </div>
              </div>
            </div>

            {/* Right Attributes & Technical Assessment (8 cols) */}
            <div className="md:col-span-8 space-y-6">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black text-white print:text-black">{athlete.name}</h1>
                <p className="text-yellow-400 font-bold text-sm uppercase tracking-wider mt-1 print:text-amber-700">
                  {athlete.sport} • {athlete.position}
                </p>
                <p className="text-slate-300 text-xs sm:text-sm mt-3 leading-relaxed print:text-slate-800">
                  {athlete.bio || "Elite represented talent under exclusive Eliada Sports Agency global management. Profile verified for professional trials and international club transfers."}
                </p>
              </div>

              {/* Performance Radar & Attributes Breakdown */}
              <div className="p-5 rounded-2xl bg-slate-900/60 border border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-4 items-center print:bg-gray-50 print:border-gray-300">
                <div className="flex justify-center">
                  <AthleteRadarChart attributes={athlete.attributes} size={220} />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase text-yellow-400 print:text-amber-700">
                    <Activity className="w-4 h-4" /> Technical Rating Metrics
                  </div>
                  <div className="space-y-2 text-xs">
                    <div>
                      <div className="flex justify-between text-slate-300 print:text-slate-800 mb-1">
                        <span>Pace & Acceleration</span>
                        <span className="font-mono font-bold text-yellow-400 print:text-amber-700">{athlete.attributes?.pace || 88}/100</span>
                      </div>
                      <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden print:bg-gray-200">
                        <div className="bg-yellow-400 h-full rounded-full print:bg-amber-600" style={{ width: `${athlete.attributes?.pace || 88}%` }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-slate-300 print:text-slate-800 mb-1">
                        <span>Technical Ball Control</span>
                        <span className="font-mono font-bold text-yellow-400 print:text-amber-700">{athlete.attributes?.technical || 85}/100</span>
                      </div>
                      <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden print:bg-gray-200">
                        <div className="bg-yellow-400 h-full rounded-full print:bg-amber-600" style={{ width: `${athlete.attributes?.technical || 85}%` }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-slate-300 print:text-slate-800 mb-1">
                        <span>Tactical Vision & Positioning</span>
                        <span className="font-mono font-bold text-yellow-400 print:text-amber-700">{athlete.attributes?.tactical || 82}/100</span>
                      </div>
                      <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden print:bg-gray-200">
                        <div className="bg-yellow-400 h-full rounded-full print:bg-amber-600" style={{ width: `${athlete.attributes?.tactical || 82}%` }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-slate-300 print:text-slate-800 mb-1">
                        <span>Physicality & Stamina</span>
                        <span className="font-mono font-bold text-yellow-400 print:text-amber-700">{athlete.attributes?.physical || 86}/100</span>
                      </div>
                      <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden print:bg-gray-200">
                        <div className="bg-yellow-400 h-full rounded-full print:bg-amber-600" style={{ width: `${athlete.attributes?.physical || 86}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Career Highlights & Achievements */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 print:text-slate-600 mb-2 flex items-center gap-1.5">
                  <Award className="w-4 h-4 text-yellow-400 print:text-amber-600" /> Career Highlights & Honors
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {(athlete.achievements && athlete.achievements.length > 0
                    ? athlete.achievements
                    : [
                        "Top Goal Contributor (Regional League)",
                        "Player of the Tournament (National Youth Cup)",
                        "Invited to National Team U-20 Camp",
                        "Scouting Top 10 Prospect Index",
                      ]
                  ).map((ach, i) => (
                    <div key={i} className="p-2.5 rounded-lg bg-slate-900/40 border border-slate-800/80 text-slate-300 print:bg-white print:border-gray-200 print:text-black flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 print:bg-amber-600 shrink-0" />
                      <span>{ach}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Transfer & Confidential Inquiry Footer */}
          <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-950 border border-yellow-500/30 flex flex-col sm:flex-row items-center justify-between gap-4 print:bg-gray-100 print:border-gray-400">
            <div>
              <h5 className="font-bold text-white print:text-black text-sm">Club Transfer & Trial Inquiries</h5>
              <p className="text-slate-400 print:text-slate-600 text-xs mt-0.5">
                For confidential trial arrangements or transfer negotiations, contact the European Transfer Secretariat.
              </p>
            </div>
            <div className="text-xs font-bold text-yellow-400 print:text-black font-mono">
              transfers@eliadasportsagency.com
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
